import PropTypes from "prop-types";
import React, { useCallback, useEffect, useSyncExternalStore } from "react";
import { Link, useNavigate } from "react-router-dom";

const MD_BREAKPOINT = 768;
const mql = typeof window !== "undefined" ? window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`) : null;
function subscribeMql(cb) {
  mql?.addEventListener("change", cb);
  return () => mql?.removeEventListener("change", cb);
}
function getIsDesktop() {
  return mql?.matches ?? true;
}

function Pagination({ basePath, page, limit, total, queryParams, label }) {
  const pages = Math.ceil(total / limit);
  const navigate = useNavigate();

  const filteredParams = new URLSearchParams(queryParams);
  filteredParams.delete("page");
  const paramString = filteredParams.toString();

  const pageUrl = useCallback(
    (p) => `${basePath}?page=${p}${paramString ? `&${paramString}` : ""}`,
    [basePath, paramString],
  );

  const handleKeyNav = useCallback(
    (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (e.key === "ArrowLeft" && page > 0) {
        navigate(pageUrl(page - 1));
      } else if (e.key === "ArrowRight" && page < pages - 1) {
        navigate(pageUrl(page + 1));
      }
    },
    [page, pages, navigate, pageUrl],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyNav);
    return () => document.removeEventListener("keydown", handleKeyNav);
  }, [handleKeyNav]);

  const isDesktop = useSyncExternalStore(subscribeMql, getIsDesktop);
  const midCount = isDesktop ? 5 : 3;
  const half = Math.floor(midCount / 2);

  const from = limit * page + 1;
  const to = Math.min(limit * (page + 1), total);

  return (
    <div className="d-flex align-items-center">
      <div className="me-auto">
        {from}&ndash;{to} / {total} <span className="d-none d-md-inline">{label}</span>
      </div>
      {pages > 1 && (
        <ul className="pagination mb-0 justify-content-center">
          <li className={`page-item ${page === 0 ? "active" : ""}`}>
            <Link className="page-link" to={pageUrl(0)}>
              1
            </Link>
          </li>
          {pages > midCount && (
            <li className={`page-item ${page < 1 ? "disabled" : ""}`}>
              <Link className="page-link" to={pageUrl(page - 1)}>
                {"<"}
              </Link>
            </li>
          )}
          {Array(pages)
            .fill(0)
            .map((_, i) => (
              <li key={`page-${i}`} className={`page-item ${page === i ? "active" : ""}`}>
                <Link className="page-link" to={pageUrl(i)}>
                  {i + 1}
                </Link>
              </li>
            ))
            .slice(
              Math.max(Math.min(page - half, pages - midCount), 1),
              Math.min(Math.max(midCount, page + half + 1), pages - 1),
            )}
          {pages > midCount && (
            <li className={`page-item ${page + 1 > pages - 1 ? "disabled" : ""}`}>
              <Link className="page-link" to={pageUrl(page + 1)}>
                {">"}
              </Link>
            </li>
          )}
          <li className={`page-item ${page === pages - 1 ? "active" : ""}`}>
            <Link className="page-link" to={pageUrl(pages - 1)}>
              {pages}
            </Link>
          </li>
        </ul>
      )}
    </div>
  );
}

Pagination.propTypes = {
  basePath: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  queryParams: PropTypes.instanceOf(URLSearchParams).isRequired,
  label: PropTypes.string.isRequired,
};

export default Pagination;
